import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Box, Button, Checkbox, Collapse, FormControlLabel, Icon, TextField, Typography } from '@material-ui/core';
import { Alert, AlertTitle, Autocomplete } from '@material-ui/lab';
import { AppSnackbar, ConfigFieldControl, ConfigTextField, CoreEvent } from '@sierralabs/core-ui';
import { applyQueryFilters, useQueryContext } from '@sierralabs/query-ui';
import QueryCsvExport from '../../common/csv/QueryCsvExport';
import { ChartItemsDialog } from '../../dialogs/ChartItemsDialog';
import { useConfigComponent } from '../../hooks/component/useConfigComponent';
import { useSubscribeComponentChanges } from '../../hooks/component/useSubscribeComponentChanges';
import { useSiteEvent } from '../../hooks/event/useSiteEvent';
import { useFindNearestComponent } from '../../hooks/site-route/useFindNearestComponent';
import { useSiteTablePagination } from '../../hooks/site-table/useSiteTablePagination';
import { useSiteContext } from '../../SiteContext';
import { SiteComponent } from '../SiteComponentPlugin';
const StyledAlert = styled(Alert) `
  .MuiAlertTitle-root {
    font-size: 15px;
    font-weight: 600;
  }
`;
function isDeepSelected(component, id) {
    return (component.id === id ||
        (component.children || []).some(child => isDeepSelected(child, id)));
}
export var ChartType;
(function (ChartType) {
    ChartType["Bar"] = "bar-chart";
    ChartType["Line"] = "line-chart";
    ChartType["Pie"] = "pie-chart";
})(ChartType || (ChartType = {}));
export function useChartComponent(component) {
    /**
     * wrap array in useRef to avoid re-evaluating hook
     */
    const types = useRef([ChartType.Bar, ChartType.Line, ChartType.Pie]).current;
    return useFindNearestComponent(types, component);
}
export var ChartItemsEvent;
(function (ChartItemsEvent) {
    ChartItemsEvent["OpenDialog"] = "chart-items:open-dialog";
})(ChartItemsEvent || (ChartItemsEvent = {}));
export const SiteChartItemsDialog = props => {
    var _a, _b;
    const component = useSubscribeComponentChanges(ChartItemsDialogPlugin, props);
    const { editing, selectedComponentRef } = useSiteContext();
    const { queries } = useQueryContext();
    const chartEntity = useChartComponent(component);
    const { pagination, setPagination } = useSiteTablePagination();
    const [open, setOpen] = useState(false);
    const [error, setError] = useState();
    const [filter, setFilter] = useState();
    const [csvExportProps, setCsvExportProps] = useState();
    const { title, disableCsvExport, csvExportConfig } = component.props || {};
    // use the table props from children
    const tableComponent = (_a = component.children) === null || _a === void 0 ? void 0 : _a.find((c) => c.slot === 'table');
    // use events from table component
    const tableSiteEvent = useSiteEvent(tableComponent || component);
    const queryId = (_b = tableComponent === null || tableComponent === void 0 ? void 0 : tableComponent.queryIds) === null || _b === void 0 ? void 0 : _b[0];
    const query = queryId
        ? queries.find(query => query.id === queryId)
        : undefined;
    const { hidePagination = true } = (tableComponent === null || tableComponent === void 0 ? void 0 : tableComponent.props) || {};
    // reveal dialog when component or descendant is selected during edit mode
    const isOpen = open ||
        !!(editing &&
            selectedComponentRef.current &&
            isDeepSelected(component, selectedComponentRef.current.id));
    const filterExpressionMap = useMemo(() => {
        const expressionMap = { queryType: 'select' };
        if (!expressionMap.wheres) {
            expressionMap.wheres = [];
        }
        if (filter && filter.conditions.length) {
            for (const condition of filter.conditions) {
                const where = {
                    type: 'and',
                    condition: {
                        operator: condition.value == null || condition.value == 'null'
                            ? 'isNull'
                            : (condition === null || condition === void 0 ? void 0 : condition.operator)
                                ? condition.operator
                                : 'equal',
                        parameters: condition.operator === 'between'
                            ? [condition.field, ...condition.value]
                            : [condition.field, condition.value]
                    }
                };
                expressionMap.wheres.push(where);
            }
        }
        return applyQueryFilters(expressionMap, Object.assign(Object.assign({}, pagination), { paginated: !hidePagination }));
    }, [filter, hidePagination, pagination]);
    // handle open dialog event
    useEffect(() => {
        return CoreEvent.handle(ChartItemsEvent.OpenDialog, data => {
            if (data &&
                data.conditions.length &&
                (chartEntity === null || chartEntity === void 0 ? void 0 : chartEntity.id) &&
                data.id === chartEntity.id) {
                setFilter(data);
                setOpen(true);
            }
        });
    }, [chartEntity === null || chartEntity === void 0 ? void 0 : chartEntity.id]);
    return (React.createElement(SiteComponent, Object.assign({ plugin: ChartItemsDialogPlugin }, props),
        isOpen && (React.createElement(ChartItemsDialog, { title: title, open: isOpen, component: component, tableComponent: tableComponent, filterExpressionMap: filterExpressionMap, setPagination: setPagination, onClose: () => setOpen(false), onRowClick: row => {
                var _a;
                const eventType = 'onClick';
                tableSiteEvent.emit(eventType, row);
                // if event exists, close dialog
                const hasEvent = (_a = tableComponent === null || tableComponent === void 0 ? void 0 : tableComponent.events) === null || _a === void 0 ? void 0 : _a.some(event => event.eventType === eventType);
                hasEvent && setOpen(false);
            }, actions: disableCsvExport ? undefined : (React.createElement(Button, { color: 'primary', variant: 'contained', endIcon: React.createElement(Icon, null, "download"), onClick: () => setCsvExportProps({ export: true }) }, "Export CSV")) })),
        csvExportProps && query && (React.createElement(QueryCsvExport, { query: query, expressionMap: filterExpressionMap, returnType: hidePagination ? undefined : 'paginated', selectedFields: (csvExportConfig === null || csvExportConfig === void 0 ? void 0 : csvExportConfig.queryFields) || [], taskStatusFields: (csvExportConfig === null || csvExportConfig === void 0 ? void 0 : csvExportConfig.taskStatusFields) || [], onError: error => {
                setError(error);
                setCsvExportProps(undefined);
            }, download: csvData => {
                setCsvExportProps(undefined);
                const a = document.createElement('a');
                const blob = new Blob([csvData], {
                    type: 'text/csv;charset=utf-8'
                });
                a.textContent = 'download';
                const fileName = (csvExportConfig === null || csvExportConfig === void 0 ? void 0 : csvExportConfig.fileName) || 'Export Results';
                a.download = `${fileName}.csv`;
                a.href = URL.createObjectURL(blob);
                a.click();
                a.href = '#';
            } })),
        React.createElement(AppSnackbar, { severity: 'error', open: !!error, onClose: () => setError(undefined) }, (error === null || error === void 0 ? void 0 : error.message) || 'Unable to export document.')));
};
export const ChartItemsDialogWarning = props => {
    const { component, children } = props;
    const chartEntity = useChartComponent(component);
    return (React.createElement(Collapse, { in: !chartEntity, style: { minHeight: 'unset' } },
        React.createElement(StyledAlert, { severity: 'warning' },
            React.createElement(AlertTitle, null, "Missing Site Chart Ancestor"),
            "This component must inside ",
            React.createElement("strong", null, "Bar Chart or Pie Chart"),
            ' ',
            "component as its ancestor for it to work properly.",
            children)));
};
export const ChartItemsDialogConfigurator = props => {
    var _a, _b, _c, _d, _e, _f;
    const { component, update } = useConfigComponent(props.component);
    const { queries } = useQueryContext();
    const selectAll = '-- Select All --';
    const tableComponent = (_a = component.children) === null || _a === void 0 ? void 0 : _a.find((c) => c.slot === 'table');
    const queryId = (_b = tableComponent === null || tableComponent === void 0 ? void 0 : tableComponent.queryIds) === null || _b === void 0 ? void 0 : _b[0];
    const csvExportConfig = (_c = component.props) === null || _c === void 0 ? void 0 : _c.csvExportConfig;
    const disableCsvExport = !!((_d = component.props) === null || _d === void 0 ? void 0 : _d.disableCsvExport);
    const query = queryId
        ? queries.find(query => query.id === queryId)
        : undefined;
    const csvQueryFields = (((_e = query === null || query === void 0 ? void 0 : query.config) === null || _e === void 0 ? void 0 : _e.selects) || []).map(select => select.selection);
    return (React.createElement(React.Fragment, null,
        React.createElement(ChartItemsDialogWarning, { component: component }),
        React.createElement(ConfigFieldControl, null,
            React.createElement(ConfigTextField, { fullWidth: true, label: 'Dialog Title', value: ((_f = component.props) === null || _f === void 0 ? void 0 : _f.title) || '', onChange: event => update('props.title', event.target.value) })),
        React.createElement(Box, { mt: 1 },
            React.createElement(Typography, { variant: 'overline' }, "CSV Export")),
        React.createElement(ConfigFieldControl, null,
            React.createElement(FormControlLabel, { label: 'Disable CSV Export', control: React.createElement(Checkbox, { size: 'small', color: 'primary', className: 'checkbox', checked: disableCsvExport, onChange: (_, checked) => {
                        update('props.disableCsvExport', checked);
                    } }) })),
        React.createElement(ConfigFieldControl, null,
            React.createElement(ConfigTextField, { fullWidth: true, size: 'small', variant: 'outlined', label: 'CSV Export File Name', disabled: disableCsvExport, value: (csvExportConfig === null || csvExportConfig === void 0 ? void 0 : csvExportConfig.fileName) || '', onChange: event => {
                    if (!csvExportConfig) {
                        update('props.csvExportConfig', {});
                    }
                    update('props.csvExportConfig.fileName', event.target.value);
                } })),
        React.createElement(ConfigFieldControl, null,
            React.createElement(Autocomplete, { multiple: true, fullWidth: true, disabled: disableCsvExport, options: [selectAll, ...csvQueryFields], value: (csvExportConfig === null || csvExportConfig === void 0 ? void 0 : csvExportConfig.queryFields) || [], onChange: (_, values) => {
                    if (!csvExportConfig) {
                        update('props.csvExportConfig', {});
                    }
                    // handle select all fields
                    const queryFields = values.includes(selectAll)
                        ? Array.from(csvQueryFields)
                        : values;
                    update('props.csvExportConfig.queryFields', queryFields);
                }, renderInput: params => (React.createElement(TextField, Object.assign({}, params, { fullWidth: true, size: 'small', variant: 'outlined', label: 'CSV Fields', helperText: 'Fields to be included in export' }))) })),
        React.createElement(ConfigFieldControl, null,
            React.createElement(Autocomplete, { multiple: true, fullWidth: true, disabled: disableCsvExport, options: [selectAll, ...csvQueryFields], value: (csvExportConfig === null || csvExportConfig === void 0 ? void 0 : csvExportConfig.taskStatusFields) || [], onChange: (_, values) => {
                    if (!csvExportConfig) {
                        update('props.csvExportConfig', {});
                    }
                    const taskStatusFields = values.includes(selectAll)
                        ? Array.from(csvQueryFields)
                        : values;
                    update('props.csvExportConfig.taskStatusFields', taskStatusFields);
                }, renderInput: params => (React.createElement(TextField, Object.assign({}, params, { fullWidth: true, size: 'small', variant: 'outlined', label: 'Task Status Fields', helperText: 'Task status fields to parse' }))) }))));
};
export const ChartItemsDialogPlugin = {
    name: 'Chart Items Dialog',
    type: 'chart-items-dialog',
    component: SiteChartItemsDialog,
    configurator: ChartItemsDialogConfigurator,
    slots: [{ name: 'Table', type: 'table', slot: 'table' }]
};
//# sourceMappingURL=ChartItemsDialogPlugin.js.map